import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, ArrowRight, CheckCircle2, Eye, EyeOff, Users, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import useAuthStore from '@/stores/authStore';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, guestLogin, isLoading, clearError, isAuthenticated } = useAuthStore();

  const [phone, setPhone]             = useState('+91');
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // OTP recovery
  const [showOtp, setShowOtp]         = useState(false);
  const [otpSent, setOtpSent]         = useState(false);
  const [otpCode, setOtpCode]         = useState('');
  const [otpLoading, setOtpLoading]   = useState(false);
  const [devOtp, setDevOtp]           = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      const { isAdmin } = useAuthStore.getState();
      navigate(isAdmin() ? '/admin' : '/admin/profile', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const normalizePhone = (p) => {
    if (!p) return '';
    const d = p.replace(/[\s\-().]/g, '').trim();
    if (!d) return '';
    if (d.startsWith('+')) return d;
    if (/^91\d{10}$/.test(d)) return '+' + d;
    if (/^\d{10}$/.test(d)) return '+91' + d;
    return d;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    clearError();
    const normPhone = normalizePhone(phone);
    if (!normPhone || normPhone.replace(/\s/g, '').length < 10) {
      toast.error('Please enter a valid phone number'); return;
    }
    if (!password) { toast.error('Please enter your password'); return; }
    if (normPhone !== phone) setPhone(normPhone);
    const result = await login(normPhone, password);
    if (result.success) {
      if (result.mustChangePassword) {
        navigate('/change-password', { replace: true });
      } else {
        const firstName = (result.userName || '').split(' ')[0];
        toast.success(firstName ? `Welcome back, ${firstName}!` : 'Welcome back!');
        const { isAdmin } = useAuthStore.getState();
        navigate(isAdmin() ? '/admin' : '/admin/profile', { replace: true });
      }
    } else {
      toast.error(result.error || 'Incorrect phone or password');
    }
  };

  const handleRequestOtp = async () => {
    const normPhone = normalizePhone(phone);
    if (!normPhone || normPhone.replace(/\s/g,'').length < 10) {
      toast.error('Enter your phone number first'); return;
    }
    setOtpLoading(true);
    try {
      const res = await fetch('/api/auth/otp/request', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normPhone }),
      });
      const data = await res.json();
      setOtpSent(true);
      if (data.dev_otp) setDevOtp(data.dev_otp);
      toast.success('OTP sent!');
    } catch { toast.error('Failed to send OTP'); }
    finally { setOtpLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode.trim()) { toast.error('Enter the OTP code'); return; }
    const normPhone = normalizePhone(phone);
    setOtpLoading(true);
    try {
      const res = await fetch('/api/auth/otp/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normPhone, code: otpCode }),
      });
      const data = await res.json();
      if (data.tokens) {
        localStorage.setItem('btccp_access_token', data.tokens.access_token);
        if (data.tokens.refresh_token) localStorage.setItem('btccp_refresh_token', data.tokens.refresh_token);
        useAuthStore.setState({
          user: data.me, isAuthenticated: true,
          activeOrgSlug: data.me?.memberships?.[0]?.slug || 'btcc',
          activeOrgId: data.active_organization_id || 'org_btcc',
          mustChangePassword: false,
        });
        toast.success('Logged in via OTP!');
        const { isAdmin } = useAuthStore.getState();
        navigate(isAdmin() ? '/admin' : '/admin/profile', { replace: true });
      } else {
        toast.error(data.detail || 'Invalid OTP');
      }
    } catch { toast.error('OTP verification failed'); }
    finally { setOtpLoading(false); }
  };

  const handleGuestLogin = async () => {
    const result = await guestLogin();
    if (result.success) {
      toast.success('Viewing as public viewer');
      navigate('/admin', { replace: true });
    } else {
      toast.error('Could not start public view. Try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#06080d] flex" data-testid="login-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/7392850/pexels-photo-7392850.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}/>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f44]/90 via-[#06080d]/70 to-[#06080d]"/>
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-center">
          <p className="text-sm font-bold text-[#f5c518] uppercase tracking-[0.3em] mb-2">Circles Sports League</p>
          <h1 className="wordmark text-5xl mb-4">BTCCP</h1>
          <p className="text-xl text-[#8a93a3] max-w-md">Multi-Org Sports Tournament Platform</p>
          <div className="mt-8 flex flex-col items-center gap-3 text-sm text-[#8a93a3]">
            {['Admin Dashboard', 'Live Scoring', 'Tournament Management'].map(f => (
              <span key={f} className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#22c55e]"/>{f}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 lg:p-12">
        <div className="lg:hidden mb-8 text-center">
          <h1 className="wordmark text-3xl mb-2">BTCCP</h1>
        </div>

        <div className="w-full max-w-md">
          <div className="bg-[#10131c] border border-[#252a37] rounded-[18px] p-8 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">

            <div className="text-center mb-7">
              <h2 className="text-2xl font-bold text-[#f0f2f7] mb-1">
                {showOtp ? 'OTP Recovery' : 'Welcome Back'}
              </h2>
              <p className="text-[#8a93a3] text-sm">
                {showOtp ? 'Enter your phone to receive a one-time code' : 'Sign in with your phone number'}
              </p>
            </div>

            {/* Phone input — always shown */}
            <div className="mb-4">
              <label className="text-sm font-medium text-[#8a93a3] mb-1.5 block">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8a93a3]"/>
                <Input
                  type="tel" value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onBlur={e => { const n = normalizePhone(e.target.value); if (n !== e.target.value) setPhone(n); }}
                  placeholder="+91 98765 43210"
                  className="pl-10 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3] focus-visible:ring-0 focus:border-[#f5c518]"
                  data-testid="login-phone-input"
                />
              </div>
            </div>

            {!showOtp ? (
              /* ── Password mode ── */
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#8a93a3] mb-1.5 block">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8a93a3]"/>
                    <Input
                      type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 pr-10 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3] focus-visible:ring-0 focus:border-[#f5c518]"
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a93a3] hover:text-[#f0f2f7]">
                      {showPassword ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                    </button>
                  </div>
                  <p className="text-xs text-[#8a93a3] mt-1.5">
                    Member default: <code className="bg-[#1d2230] px-1.5 py-0.5 rounded text-[#f5c518] font-mono">CIRCLES</code>
                  </p>
                </div>
                <Button type="submit" disabled={isLoading}
                  className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-bold hover:bg-[#f7d24a]"
                  data-testid="login-submit-button">
                  {isLoading
                    ? <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-[#0b0f18] border-t-transparent rounded-full"/>Signing in...</span>
                    : <span className="flex items-center gap-2">Sign In <ArrowRight className="h-4 w-4"/></span>}
                </Button>
                <button type="button" onClick={() => { setShowOtp(true); setOtpSent(false); setDevOtp(''); setOtpCode(''); }}
                  className="w-full text-xs text-[#8a93a3] hover:text-[#f5c518] text-center transition-colors">
                  Forgot password? Use OTP recovery →
                </button>
              </form>
            ) : (
              /* ── OTP recovery mode ── */
              <div className="space-y-4">
                {devOtp && (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.25)] rounded-xl">
                    <KeyRound className="h-4 w-4 text-[#f5c518] flex-shrink-0"/>
                    <p className="text-sm text-[#f0f2f7]">Your OTP: <code className="font-mono font-bold text-[#f5c518] text-lg">{devOtp}</code></p>
                  </div>
                )}
                {!otpSent ? (
                  <Button onClick={handleRequestOtp} disabled={otpLoading}
                    className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-bold hover:bg-[#f7d24a]">
                    {otpLoading ? 'Sending...' : 'Send OTP to Phone'}
                  </Button>
                ) : (
                  <>
                    <div>
                      <label className="text-sm font-medium text-[#8a93a3] mb-1.5 block">Enter OTP</label>
                      <Input type="text" value={otpCode} onChange={e => setOtpCode(e.target.value)}
                        placeholder="6-digit code" maxLength={6}
                        className="h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] text-center text-xl tracking-widest font-mono focus-visible:ring-0 focus:border-[#f5c518]"
                        data-testid="otp-code-input"/>
                    </div>
                    <Button onClick={handleVerifyOtp} disabled={otpLoading}
                      className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-bold hover:bg-[#f7d24a]">
                      {otpLoading ? 'Verifying...' : <span className="flex items-center gap-2">Verify & Login <ArrowRight className="h-4 w-4"/></span>}
                    </Button>
                    <button onClick={handleRequestOtp}
                      className="w-full text-xs text-[#8a93a3] hover:text-[#f5c518] text-center">Resend OTP</button>
                  </>
                )}
                <button onClick={() => { setShowOtp(false); setOtpSent(false); setDevOtp(''); }}
                  className="w-full text-xs text-[#8a93a3] hover:text-[#f0f2f7] text-center">
                  ← Back to password login
                </button>
              </div>
            )}

            {/* Divider + Guest */}
            {!showOtp && (
              <>
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-[#252a37]"/>
                  <span className="text-xs text-[#8a93a3]">or</span>
                  <div className="flex-1 h-px bg-[#252a37]"/>
                </div>
                <button onClick={handleGuestLogin} disabled={isLoading}
                  data-testid="guest-login-button"
                  className="w-full h-12 rounded-xl border border-[#252a37] bg-[#161a26] text-[#f0f2f7] hover:bg-[#1d2230] hover:border-[rgba(245,197,24,0.3)] transition-colors flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-60">
                  <Users className="h-4 w-4 text-[#8a93a3]"/>
                  Continue as Public Viewer
                </button>
                <p className="text-center text-[10px] text-[#8a93a3] mt-2">
                  View tournaments, standings &amp; rankings without signing in
                </p>
              </>
            )}
          </div>

          <p className="text-center text-xs text-[#8a93a3] mt-6">
            Software by <span className="font-semibold">@Sandeep Maheshwari</span>
          </p>
        </div>
      </div>
    </div>
  );
}
